<template>
  <div class="chip-select" :class="{'d-flex gap-gd-1 flex-wrap': mode == 'mixed'}">
    <!-- Mode: dropdown — single chip button with dropdown list -->
    <div v-if="mode === 'dropdown'" class="chip-select--dropdown position-relative" :class="{ 'pointer-events-none': disabled }">
      <button
        :key="isOpen"
        class="chip-select__trigger d-flex fs-sa-14 fw-400 text-default align-items-center gap-gd-half cursor-pointer border-radius-9"
        :class="{ 'pointer-events-none': disabled }"
        @click="!disabled ? toggleDropdown() : null"
      >
        <span v-if="isLabelAndValue && label">{{ label }}:</span>
        <span>{{ selectedLabel || label }}</span>
        <i class="ml-gd-1 fal fs-sa-16 text-black-hydrocarbon" :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </button>
      <div v-if="isOpen" class="chip-select__dropdown bg-white p-gd-1">
        <div
          v-for="option in options"
          :key="getOptionValue(option)"
          class="chip-select__option fs-sa-12 fw-400 cursor-pointer bg-hover-blue-light p-gd-1"
          :class="{ 'text-blue2-dark bg-blue2-light': isSelected(option) }"
          @click="!disabled ? selectOption(option) : null"
        >
          <span class="fs-sa-14 fw-400">{{ getOptionLabel(option) }}</span>
        </div>
      </div>
    </div>

    <!-- Mode: mixed — each option renders as tag or dropdown based on option._mode -->
    <template v-else-if="mode === 'mixed'">
      <template v-for="option in options">
        <!-- Dropdown item -->
        <div
          v-if="option._mode === 'dropdown'"
          :key="'dd-' + getOptionValue(option)"
          class="chip-select--dropdown position-relative"
          :class="{ 'pointer-events-none': disabled }"
        >
          <button
            :key="openDropdownId === getOptionValue(option)"
            class="chip-select__trigger d-flex fs-sa-14 fw-400 text-default align-items-center gap-gd-half cursor-pointer border-radius-9"
            :class="{ 'pointer-events-none': disabled }"
            @click="!disabled ? toggleMixedDropdown(getOptionValue(option)) : null"
          >
            <span>{{ getOptionLabel(option) }}</span>
            <i class="ml-gd-1 fal fs-sa-16 text-black-hydrocarbon" :class="openDropdownId === getOptionValue(option) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
          <div v-if="openDropdownId === getOptionValue(option)" class="chip-select__dropdown bg-white pa-gd-1">
            <div
              v-for="subOption in (option._subOptions || [])"
              :key="getSubOptionValue(subOption)"
              class="chip-select__option fs-sa-12 fw-400 cursor-pointer bg-hover-blue-light p-gd-1"
              @click="!disabled ? selectMixedSubOption(option, subOption) : null"
            >
              <span class="fs-sa-14 fw-400">{{ getSubOptionLabel(subOption) }}</span>
            </div>
          </div>
        </div>

        <!-- Tag item (default) -->
        <button
          v-else
          :key="'tag-' + getOptionValue(option)"
          :disabled="disabled"
          class="chip-select__tag bg-white border-radius-rounder"
          :class="isSelected(option) ? 'active' : 'text-paragraph'"
          @click="!disabled ? selectOption(option) : null"
        >
          <img v-if="getOptionIcon(option)" :src="getOptionIcon(option)" class="chip-select__icon mr-gd-1" alt="">
          <span class="fs-sa-14 fw-400">{{ getOptionLabel(option) }}</span>
        </button>
      </template>
    </template>

    <!-- Mode: tags — multiple chip buttons inline -->
    <div v-else class="chip-select--tags d-flex align-items-center flex-wrap gap-gd-1">
      <button
        :disabled="disabled"
        v-for="option in options"
        :key="getOptionValue(option)"
        class="chip-select__tag bg-white border-radius-rounder"
        :class="isSelected(option)
          ? 'active'
          : 'text-paragraph'"
        @click="!disabled ? selectOption(option) : null"
      >
        <img v-if="getOptionIcon(option)" :src="getOptionIcon(option)" class="chip-select__icon mr-gd-1" alt="">
        <span class="fs-sa-14 fw-400">{{ getOptionLabel(option) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChipSelect',
  props: {
    /**
     * Display mode
     * - 'dropdown': single chip button with a dropdown list
     * - 'tags': multiple chip buttons displayed inline
     */
    mode: {
      type: String,
      default: 'dropdown',
      validator: (v) => ['dropdown', 'tags', 'mixed'].includes(v)
    },
    /**
     * Array of option objects
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Currently selected value (matches optionValue key)
     */
    value: {
      type: [String, Number],
      default: null
    },
    /**
     * Key name for option value (default: 'id')
     */
    optionValue: {
      type: String,
      default: 'id'
    },
    /**
     * Key name for option label (default: 'name')
     */
    optionLabel: {
      type: String,
      default: 'name'
    },
    /**
     * Optional custom function to format option label.
     * Receives the option object, should return a string.
     * When provided, takes priority over optionLabel key.
     */
    optionLabelFormatter: {
      type: Function,
      default: null
    },
    /**
     * Key name for option icon (image src). When set, an <img> is shown before the label.
     */
    optionIcon: {
      type: String,
      default: ''
    },
    /**
     * Optional label prefix shown before selected value (dropdown mode only)
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Disable interaction
     */
    disabled: {
      type: Boolean,
      default: false
    },
    isLabelAndValue: {
      type: Boolean,
      default: false
    },
    /**
     * Key name for sub-option value (mixed mode, default: 'id')
     */
    subOptionValue: {
      type: String,
      default: 'id'
    },
    /**
     * Key name for sub-option label (mixed mode, default: 'name')
     */
    subOptionLabel: {
      type: String,
      default: 'name'
    }
  },
  data() {
    return {
      isOpen: false,
      openDropdownId: null
    };
  },
  computed: {
    selectedLabel() {
      const selected = this.options.find(o => this.getOptionValue(o) === this.value);
      return selected ? this.getOptionLabel(selected) : (this.options.length ? this.getOptionLabel(this.options[0]) : '');
    }
  },
  methods: {
    getOptionValue(option) {
      return option[this.optionValue];
    },
    getOptionLabel(option) {
      if (this.optionLabelFormatter) return this.optionLabelFormatter(option);
      return option[this.optionLabel];
    },
    getOptionIcon(option) {
      return this.optionIcon ? option[this.optionIcon] : '';
    },
    isSelected(option) {
      return this.getOptionValue(option) === this.value;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectOption(option) {
      this.$emit('input', this.getOptionValue(option));
      this.$emit('change', option);
      this.isOpen = false;
      this.openDropdownId = null;
    },
    /* Mixed mode helpers */
    toggleMixedDropdown(id) {
      this.openDropdownId = this.openDropdownId === id ? null : id;
    },
    getSubOptionValue(subOption) {
      return subOption[this.subOptionValue];
    },
    getSubOptionLabel(subOption) {
      return subOption[this.subOptionLabel];
    },
    selectMixedSubOption(parentOption, subOption) {
      this.$emit('sub-change', { parent: parentOption, sub: subOption });
      this.openDropdownId = null;
    }
  },
  beforeDestroy() {
    this.isOpen = false;
    this.openDropdownId = null;
  }
};
</script>

<style lang="scss" scoped>
/* ===== Dropdown mode ===== */
.chip-select--dropdown {
  &.pointer-events-none {
    pointer-events: none !important;
  }
}

.chip-select__trigger {
  transition: all 0.2s;
  background: #FFFFFF;
  border: 1px solid #DEE3E6;
  border-radius: 20px;
  padding: 6px 16px;
    span {
    line-height: 1
  }

  &.active,
  &:hover,
  &:focus {
    color: #0C739D !important;
    border: 1.5px solid transparent;
    background: linear-gradient(white, white) padding-box, linear-gradient(61deg, #28BEE3 0%, #7A5AF8 49.73%, #EE46BC 99.45%) border-box !important;
  }
}

.chip-select__dropdown {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #DEE3E6;
  border-radius: 16px;
  max-width: 267px;

  .chip-select__option {
    transition: background-color 0.2s;

    &:first-child {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }

    &:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
}

.chip-select__icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* ===== Tags mode ===== */
.chip-select__tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  transition: all 0.2s;
  white-space: nowrap;
  cursor: pointer;
  border: 1.5px solid #DEE3E6;

  span {
    line-height: 1;
  }

  &:hover,
  &.hover,
  &:focus,
  &.active {
    color: #0265AC !important;
    border: 1.5px solid transparent;
    background: linear-gradient(white, white) padding-box, linear-gradient(61deg, #28BEE3 0%, #7A5AF8 49.73%, #EE46BC 99.45%) border-box !important;

    span {
      color: #0265AC;
    }
  }
}
</style>
